
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Filter } from 'lucide-react';
import { DocumentMetadata } from '../../api/types';

interface DocumentsTableProps {
    documents: DocumentMetadata[];
}

type SortOption = 'Urgency' | 'A → Z' | 'Z → A' | 'Date Created' | 'Date Updated';

const DocumentsTable: React.FC<DocumentsTableProps> = ({ documents }) => {
    const [sortOption, setSortOption] = useState<SortOption>('Urgency');
    const [isSortOpen, setIsSortOpen] = useState(false);
    const sortRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
                setIsSortOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Sorting Logic
    const sortedDocuments = [...documents].sort((a, b) => {
        switch (sortOption) {
            case 'A → Z':
                return a.name.localeCompare(b.name);
            case 'Z → A':
                return b.name.localeCompare(a.name);
            case 'Date Created':
            case 'Date Updated':
                // Assuming uploadDate is MM/DD/YYYY for simplicity in this mock
                return new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime();
            case 'Urgency':
            default:
                // Mock urgency: keeping original order or specific logic
                return 0;
        }
    });

    const handleSortSelect = (option: SortOption) => {
        setSortOption(option);
        setIsSortOpen(false);
    };

    return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-[400px]"> {/* Added min-h to allow dropdown scroll if needed */}
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h2 className="text-base font-bold text-gray-900">All Documents</h2>
                <div className="flex gap-3">
                    {/* Sort Dropdown */}
                    <div className="relative" ref={sortRef}>
                        <button
                            onClick={() => setIsSortOpen(!isSortOpen)}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 bg-white font-medium w-[160px] justify-between"
                        >
                            <span>Sort by: {sortOption}</span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isSortOpen && (
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50 animate-in fade-in zoom-in-95 duration-100">
                                <div className="px-3 py-2 text-xs font-bold text-gray-900 uppercase tracking-wider border-b border-gray-50 mb-1">
                                    Urgency
                                </div>
                                {['A → Z', 'Z → A', 'Date Created', 'Date Updated'].map((option) => (
                                    <button
                                        key={option}
                                        onClick={() => handleSortSelect(option as SortOption)}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 text-gray-700 ${sortOption === option ? 'font-bold bg-gray-50' : ''}`}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Filter Button */}
                    <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 bg-white font-medium">
                        Filter
                        <Filter className="w-4 h-4 text-gray-400" />
                    </button>
                </div>
            </div>

            <table className="w-full text-left text-sm">
                <thead className="text-gray-500 font-medium border-b border-gray-100 bg-white">
                    <tr>
                        <th className="py-4 pl-6 font-semibold w-[30%]">Name</th>
                        <th className="py-4 font-semibold w-[15%]">File type</th>
                        <th className="py-4 font-semibold w-[25%]">Document Type</th>
                        <th className="py-4 font-semibold w-[20%]">Upload date</th>
                        <th className="py-4 w-[10%]"></th>
                    </tr>
                </thead>
                <tbody className="text-gray-700">
                    {sortedDocuments.map((doc) => (
                        <tr key={doc.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors group">
                            <td className="py-4 pl-6">
                                <div className="font-bold text-gray-900">{doc.name}</div>
                                <div className="text-xs text-gray-400">{doc.size}</div>
                            </td>
                            <td className="py-4 text-gray-500">{doc.fileType}</td>
                            <td className="py-4">
                                <span className={`px-2.5 py-1 rounded text-xs font-bold whitespace-nowrap ${doc.docType === 'Bank Transaction' ? 'bg-purple-100 text-purple-700' :
                                    doc.docType === 'Balance Sheet' ? 'bg-purple-100 text-purple-700' :
                                        doc.docType === 'P&L' ? 'bg-purple-100 text-purple-700' :
                                            'bg-gray-100 text-gray-700'
                                    }`}>
                                    {doc.docType}
                                </span>
                            </td>
                            <td className="py-4 text-gray-500">{doc.uploadDate}</td>
                            <td className="py-4 text-right pr-6 text-gray-400 font-bold cursor-pointer hover:text-gray-600">...</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default DocumentsTable;
